/**
 * Letter-for-letter Unicode "fonts": monospace, double-struck and squared text.
 *
 * None of these is a font. Each style is a run of separately encoded characters —
 * mostly from the Mathematical Alphanumeric Symbols block (U+1D400–U+1D7FF) and the
 * Enclosed Alphanumeric Supplement (U+1F100–U+1F1FF) — and converting text means
 * swapping every Basic Latin letter or digit for its counterpart. Anything the style
 * does not cover passes through unchanged, so the output is always the same length in
 * user-perceived characters as the input.
 */

export type StyleMap = ReadonlyMap<string, string>;

const CODE_UPPER_A = 0x41;
const CODE_LOWER_A = 0x61;
const CODE_ZERO = 0x30;
const LETTER_COUNT = 26;
const DIGIT_COUNT = 10;

interface AlphabetStart {
  /** The code point that replaces "A". */
  upper: number;
  /** The code point that replaces "a"; omit to map lowercase onto `upper`. */
  lower?: number;
  /** The code point that replaces "0"; omit when the style has no digits. */
  digit?: number;
  /** Letters whose slot in the block is reserved, with the character that fills it. */
  exceptions?: Readonly<Record<string, string>>;
}

function buildStyleMap({
  upper,
  lower,
  digit,
  exceptions,
}: AlphabetStart): StyleMap {
  const map = new Map<string, string>();
  for (let index = 0; index < LETTER_COUNT; index++) {
    const styledUpper = String.fromCodePoint(upper + index);
    map.set(String.fromCharCode(CODE_UPPER_A + index), styledUpper);
    map.set(
      String.fromCharCode(CODE_LOWER_A + index),
      lower === undefined ? styledUpper : String.fromCodePoint(lower + index)
    );
  }
  if (digit !== undefined) {
    for (let index = 0; index < DIGIT_COUNT; index++) {
      map.set(
        String.fromCharCode(CODE_ZERO + index),
        String.fromCodePoint(digit + index)
      );
    }
  }
  for (const [letter, replacement] of Object.entries(exceptions ?? {})) {
    map.set(letter, replacement);
  }
  return map;
}

/**
 * Mathematical Monospace: capitals U+1D670–U+1D689, small letters U+1D68A–U+1D6A3,
 * digits U+1D7F6–U+1D7FF. The block has no gaps, so no exceptions are needed.
 */
export const MONOSPACE_MAP: StyleMap = buildStyleMap({
  upper: 0x1_d6_70,
  lower: 0x1_d6_8a,
  digit: 0x1_d7_f6,
});

/**
 * The seven double-struck capitals encoded in Letterlike Symbols (U+2100–U+214F) years
 * before the mathematical alphabets. Their slots in the Mathematical Alphanumeric
 * Symbols block (U+1D53A, U+1D53F, U+1D545, U+1D547–U+1D549 and U+1D551) are reserved
 * and permanently unassigned, so the older characters must be used instead.
 */
export const DOUBLE_STRUCK_EXCEPTIONS: Readonly<Record<string, string>> = {
  C: "ℂ",
  H: "ℍ",
  N: "ℕ",
  P: "ℙ",
  Q: "ℚ",
  R: "ℝ",
  Z: "ℤ",
};

/**
 * Mathematical Double-Struck: capitals from U+1D538 (with the seven Letterlike Symbols
 * exceptions), small letters U+1D552–U+1D56B, digits U+1D7D8–U+1D7E1.
 */
export const DOUBLE_STRUCK_MAP: StyleMap = buildStyleMap({
  upper: 0x1_d5_38,
  lower: 0x1_d5_52,
  digit: 0x1_d7_d8,
  exceptions: DOUBLE_STRUCK_EXCEPTIONS,
});

/**
 * Squared Latin capitals U+1F130–U+1F149. Unicode encodes no squared small letters or
 * squared digits, so lowercase is drawn with the capitals and digits pass through.
 */
export const SQUARED_MAP: StyleMap = buildStyleMap({ upper: 0x1_f1_30 });

/** Negative (filled) squared Latin capitals U+1F170–U+1F189. Capitals only, as above. */
export const NEGATIVE_SQUARED_MAP: StyleMap = buildStyleMap({
  upper: 0x1_f1_70,
});

/**
 * The 26 regional indicator symbols, U+1F1E6–U+1F1FF.
 *
 * They look like squared capitals, which is why some generators use them — but any two
 * in a row are read as a country code and drawn as a flag (U+1F1FA U+1F1F8 is the US flag), so
 * "USA" typed in them turns into a flag and a stray letter. This module never emits one;
 * the constant exists so the tests can say so.
 */
export const REGIONAL_INDICATOR_RANGE = [0x1_f1_e6, 0x1_f1_ff] as const;

/**
 * The four negative squared letters that are also emoji: 🅰 🅱 🅾 (the blood-type
 * buttons) and 🅿 (the parking button). Their default presentation is text, but some
 * apps draw them as colored emoji tiles anyway, which breaks up a line of otherwise
 * black squares.
 */
export const EMOJI_SQUARED_LETTERS: ReadonlySet<string> = new Set([
  "\u{1F170}",
  "\u{1F171}",
  "\u{1F17E}",
  "\u{1F17F}",
]);

/** VARIATION SELECTOR-15: asks for the text (monochrome) form of the character before it. */
export const TEXT_PRESENTATION_SELECTOR = "︎";

/** VARIATION SELECTOR-16: asks for the emoji (colored) form. */
const EMOJI_PRESENTATION_SELECTOR = "️";

/**
 * Follow each of the four emoji letters with VARIATION SELECTOR-15. Unicode defines
 * these as standardized variation sequences (1F170 FE0E is "text style"), so a
 * conforming app draws the plain glyph — though not every app honours the request. A
 * letter that already carries a selector is left alone rather than given a second one.
 */
export function requestTextPresentation(text: string): string {
  const characters = [...text];
  let output = "";
  for (const [index, character] of characters.entries()) {
    output += character;
    const next = characters[index + 1];
    if (
      EMOJI_SQUARED_LETTERS.has(character) &&
      next !== TEXT_PRESENTATION_SELECTOR &&
      next !== EMOJI_PRESENTATION_SELECTOR
    ) {
      output += TEXT_PRESENTATION_SELECTOR;
    }
  }
  return output;
}

/** Swap every character the style covers; everything else passes through. */
export function applyStyle(text: string, map: StyleMap): string {
  let output = "";
  for (const character of text) {
    output += map.get(character) ?? character;
  }
  return output;
}

const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;

/**
 * The distinct letters and digits a style left unchanged, in order of appearance — the
 * accented letters, other alphabets and (for the squared styles) digits that have no
 * styled form. Shown to the reader so a mixed result is explained rather than a mystery.
 */
export function unstyledCharacters(text: string, map: StyleMap): string[] {
  const seen = new Set<string>();
  for (const character of text) {
    if (LETTER_OR_DIGIT.test(character) && !map.has(character)) {
      seen.add(character);
    }
  }
  return [...seen];
}

const BACKTICK_RUN = /`+/g;
const LINE_BREAK = /\r\n|\r|\n/;

/**
 * The same text as Markdown code — the real, accessible way to get monospace in
 * Discord, Slack, GitHub, Reddit and most chat apps.
 *
 * One line becomes an inline code span and several lines a fenced block. The fence is
 * one backtick longer than the longest run of backticks in the text, which is the
 * CommonMark rule for code that itself contains backticks; an inline span that starts
 * or ends with a backtick gets a space of padding on each side for the same reason.
 */
export function toMarkdownCode(text: string): string {
  if (text.length === 0) {
    return "";
  }
  let longestRun = 0;
  for (const run of text.match(BACKTICK_RUN) ?? []) {
    longestRun = Math.max(longestRun, run.length);
  }
  if (LINE_BREAK.test(text)) {
    const fence = "`".repeat(Math.max(3, longestRun + 1));
    return `${fence}\n${text}\n${fence}`;
  }
  const fence = "`".repeat(longestRun + 1);
  const padded =
    text.startsWith("`") || text.endsWith("`") ? ` ${text} ` : text;
  return `${fence}${padded}${fence}`;
}
